import type { Asset, MorphPairing, Scene } from '@lumen5/luminary';
import {
    deriveMatchCutPairings,
    distanceBetween,
    getMorphableAssets,
    getTextMorphUnits,
    sharesNativelyMorphingAsset,
} from '@lumen5/luminary';

import {
    getPrePackagedMorphPosition,
    getAdjacentMorphDisqualifiedReason,
    hasAppliedMatchCut,
} from 'js/src/luminary/luminary-selectors';

export type MorphTechniqueId = 'text';

export type MorphDisqualifiedReason =
    | 'no-morphable-assets'
    | 'incompatible-structure'
    | 'below-threshold'
    | 'shares-morphable-content'
    | 'consecutive-morph-limit'
    | 'adjacent-foreign-morph'
    | 'scene-sequence';

export type MorphRecommendation =
    | {
          recommended: true;
          score: number;
          techniqueId: MorphTechniqueId;
          disqualifiedReason: null;
          pairings: MorphPairing[];
      }
    | {
          recommended: false;
          score: number;
          techniqueId: MorphTechniqueId | null;
          disqualifiedReason: MorphDisqualifiedReason;
          pairings: MorphPairing[];
      };

export const DEFAULT_MORPH_SCORE_THRESHOLD = 0.5;

export function disqualifyMorph(reason: MorphDisqualifiedReason): MorphRecommendation {
    return {
        recommended: false,
        score: 0,
        techniqueId: null,
        disqualifiedReason: reason,
        pairings: [],
    };
}

// Below this fraction of the canvas diagonal, a text match-cut reads as a blink in place, not a cut.
const MIN_TEXT_TRAVEL_FRACTION = 0.04;

function referenceDiagonal(scene: Scene): number {
    const { width, height } = scene.root.referenceResolution;
    return Math.hypot(width, height);
}

function travelFraction(source: Asset, target: Asset, diagonal: number): number {
    if (diagonal === 0) return 0;
    return distanceBetween(source, target) / diagonal;
}

// Size/area is deliberately not a scoring factor — a match-cut reads fine between differently-sized text.
function scoreMatchCutPairings(
    sourceScene: Scene,
    targetScene: Scene,
    pairings: MorphPairing[],
): number {
    const sourceTextUnitCount = getTextMorphUnits(sourceScene, 'end').length;
    const targetTextUnitCount = getTextMorphUnits(targetScene, 'start').length;
    const diagonal = referenceDiagonal(sourceScene);

    const totalTravel = pairings.reduce((sum, pairing) => {
        const source = sourceScene.getAssetById(pairing.sourceId, { deep: true });
        const target = targetScene.getAssetById(pairing.targetId, { deep: true });
        if (!source || !target) {
            throw new Error('match-cut pairing references an asset not found in the given scene');
        }
        return sum + travelFraction(source, target, diagonal);
    }, 0);

    const averageTravel = totalTravel / pairings.length;
    const countMatch =
        Math.min(sourceTextUnitCount, targetTextUnitCount) /
        Math.max(sourceTextUnitCount, targetTextUnitCount);
    const travelSufficiency = averageTravel >= MIN_TEXT_TRAVEL_FRACTION ? 1 : 0;

    return countMatch * travelSufficiency;
}

export function scoreScenePairForMorph(
    sourceScene: Scene,
    targetScene: Scene,
    threshold: number = DEFAULT_MORPH_SCORE_THRESHOLD,
): MorphRecommendation {
    const sources = getMorphableAssets(sourceScene, 'end');
    const targets = getMorphableAssets(targetScene, 'start');

    if (sources.length === 0 || targets.length === 0) {
        return disqualifyMorph('no-morphable-assets');
    }

    if (sharesNativelyMorphingAsset(sources, targets)) {
        return disqualifyMorph('shares-morphable-content');
    }

    // Uses luminary's shared pairing algorithm (also run by rehydration on load) so apply and reload can't disagree.
    const pairings = deriveMatchCutPairings(sourceScene, targetScene);

    if (pairings.length === 0) {
        return disqualifyMorph('incompatible-structure');
    }

    const score = scoreMatchCutPairings(sourceScene, targetScene, pairings);

    if (score < threshold) {
        return {
            recommended: false,
            score,
            techniqueId: 'text',
            disqualifiedReason: 'below-threshold',
            pairings,
        };
    }

    // Only text is paired/scored — other morphable assets are frozen-cut by apply, not paired here.
    return {
        recommended: true,
        score,
        techniqueId: 'text',
        disqualifiedReason: null,
        pairings,
    };
}

export type MorphGapState = {
    morphSceneId: string | null;
    /** The raw flag on the scene before the gap, whether or not it currently animates anything. */
    isMorphEnabled: boolean;
    /** Whether the enabled morph has a pair of assets to animate between. */
    hasMorphMatch: boolean;
    isRecommended: boolean;
};

/**
 * Whether a morph across this gap would animate anything. Editing the scenes on either side — inserting a scene
 * between them, swapping one's media — can leave `morphEnabled` set on a gap that no longer has a pair to morph, and
 * a morph with nothing to match is indistinguishable from a cut.
 */
export function gapHasMorphMatch(fromScene: Scene, intoScene: Scene): boolean {
    // An applied match cut animates through duplicated assets, which the morphable-asset pairing deliberately skips.
    if (hasAppliedMatchCut(fromScene)) {
        return true;
    }
    return sharesNativelyMorphingAsset(
        getMorphableAssets(fromScene, 'end'),
        getMorphableAssets(intoScene, 'start'),
    );
}

export function getMorphGapState(intoScene: Scene): MorphGapState {
    const morphScene = intoScene.prevScene;
    if (!morphScene) {
        return {
            morphSceneId: null,
            isMorphEnabled: false,
            hasMorphMatch: false,
            isRecommended: false,
        };
    }
    const gapState = {
        morphSceneId: morphScene.id,
        isMorphEnabled: morphScene.morphEnabled,
        hasMorphMatch: gapHasMorphMatch(morphScene, intoScene),
    };
    if (
        getPrePackagedMorphPosition(morphScene) !== null ||
        getPrePackagedMorphPosition(intoScene) !== null
    ) {
        return { ...gapState, isMorphEnabled: false, isRecommended: false };
    }
    if (getAdjacentMorphDisqualifiedReason(morphScene)) {
        return { ...gapState, isRecommended: false };
    }
    const recommendation = scoreScenePairForMorph(morphScene, intoScene);
    return { ...gapState, isRecommended: recommendation.recommended };
}
